import { Linkedin, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';

const offices = [
  {
    country: 'Lithuania',
    company: '0g Baltics UAB',
    address: 'A. Goštauto g. 8-358',
    city: 'LT-01108 Vilnius',
  },
  {
    country: 'Latvia',
    company: '0g Baltics SIA',
    address: 'Elizabetes iela 31-8',
    city: 'LV-1010 Riga',
  },
  {
    country: 'Estonia',
    company: 'E2 Technologies OÜ',
    address: 'Lõõtsa tn 5',
    city: '11415 Tallinn',
  },
];

export const Footer = () => {
  const { t } = useLanguage();

  const navLinks = [
    { name: t('nav.solutions'), href: '/#solutions' },
    { name: t('nav.infrastructure'), href: '/#infrastructure' },
    { name: t('nav.devices'), href: '/#devices' },
    { name: t('nav.consulting'), href: '/#consulting' },
    { name: t('nav.contact'), href: '/#contact' },
  ];

  const legalLinks = [
    { name: t('footer.privacyPolicy'), href: '/privacy-policy' },
    { name: t('footer.gdpr'), href: '/privacy-policy' },
    { name: t('footer.terms'), href: '/terms' },
    { name: t('footer.imprint'), href: '/imprint' },
  ];

  return (
    <footer className="bg-slate-900 text-white">
      <div className="container-0g py-16">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <h3 className="text-2xl font-bold mb-4">0g Baltics</h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            <div className="flex items-center space-x-4">
              <a
                href="https://www.linkedin.com/company/0gbaltics"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-teal-400 transition-colors"
                data-testid="linkedin-link-footer"
              >
                <Linkedin className="h-6 w-6" />
              </a>
              <a
                href="mailto:info@0gbaltics.com"
                className="text-slate-400 hover:text-teal-400 transition-colors"
                data-testid="email-link-footer"
              >
                <Mail className="h-6 w-6" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-4">{t('footer.navigation')}</h4>
            <nav className="space-y-3">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block text-slate-300 hover:text-white transition-colors text-sm"
                  data-testid={`footer-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ))}
            </nav>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-4">{t('footer.legal')}</h4>
            <nav className="space-y-3">
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="block text-slate-300 hover:text-white transition-colors text-sm"
                  data-testid={`footer-legal-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Offices */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-4">{t('footer.offices')}</h4>
            <div className="space-y-4">
              {offices.map((office) => (
                <div 
                  key={office.country} 
                  className="office-card pl-4"
                  data-testid={`office-${office.country.toLowerCase()}`}
                >
                  <div className="flex items-start space-x-2 mb-1">
                    <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                    <span className="text-white font-semibold text-sm">{office.country}</span>
                  </div>
                  <p className="text-slate-400 text-xs ml-6">{office.company}</p>
                  <p className="text-slate-400 text-xs ml-6">{office.address}</p>
                  <p className="text-slate-400 text-xs ml-6">{office.city}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Email */}
        <div className="border-t border-slate-800 pt-8 mb-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4 text-teal-500" />
              <a 
                href="mailto:info@0gbaltics.com"
                className="text-slate-300 hover:text-white text-sm"
              >
                info@0gbaltics.com
              </a>
            </div>
            <div className="flex items-center space-x-4 text-xs text-slate-500">
              <span>IoT Solutions</span>
              <span>•</span>
              <span>LoRaWAN Infrastructure</span>
              <span>•</span>
              <span>Smart Cities</span>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-xs">
              {t('footer.copyright')}
            </p>
            <div className="flex items-center space-x-6 text-xs text-slate-500">
              <Link to="/privacy-policy" className="hover:text-slate-300 transition-colors">{t('footer.privacyPolicy')}</Link>
              <Link to="/terms" className="hover:text-slate-300 transition-colors">{t('footer.terms')}</Link>
              <Link to="/imprint" className="hover:text-slate-300 transition-colors">{t('footer.imprint')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
